import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { TextInput } from '@basic';
import { setupForDark } from '@theme';

interface TextFieldEditorProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  'onChange' | 'title'
> {
  title: ReactNode;
  placeholder: string;
  placeholderIcon: ReactNode;
  value: string;
  onChange: (value: any) => void;
  maxLength?: number;
  maxRows?: number;
  withMarkdown?: boolean;
}

const TextFieldEditor = ({
  title,
  placeholder,
  placeholderIcon,
  value,
  onChange,
  maxLength,
  maxRows = 10,
  withMarkdown = false,
  ...rest
}: TextFieldEditorProps) => {
  const [inputValue, setInputValue] = useState(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  const debouncedOnChange = useCallback(
    (value: string) => {
      // Cancel previous timeout if it exists
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Set new timeout
      debounceTimeoutRef.current = setTimeout(() => {
        onChange(value);
      }, 100);
    },
    [onChange]
  );

  const onEdit = () => {};

  const onInputChanged = (value: string) => {
    setInputValue(value);
    debouncedOnChange(value);
  };

  const onInputSaved = () => {
    // Cancel any pending debounced call since user explicitly finished
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
    onChange(inputValue);
  };

  return (
    <div
      {...rest}
      className={classNames(
        'flex flex-col',
        'rounded-sm px-sm pt-xxs pb-md',
        setupForDark('bg-primary')
      )}
    >
      <div className='flex flex-row items-center justify-between'>
        <div className={classNames('LS2M py-sm mb-xxs uppercase', setupForDark('text-tertiary'))}>
          {title}
        </div>
      </div>
      <TextInput
        value={inputValue}
        placeholder={placeholder}
        maxLength={maxLength}
        onChange={onInputChanged}
        autoFocus={false}
        onFinish={onInputSaved}
        maxRows={maxRows}
      />
    </div>
  );
};

export default TextFieldEditor;
